import { HttpEvent, HttpEventType, HttpResponse } from '@angular/common/http';
import { Component, Input, OnChanges, OnInit } from '@angular/core';
import {
  ActiveConfig,
  ImageType,
  LogodeletePayload,
  LogosResultTypeList,
  LogosTypeList,
  PopupSettingType,
  WidgetNames,
  WorkflowConfig,
  PopupSettings,
} from '@finxone-platform/shared/sys-config-types';
import { Select, Store } from '@ngxs/store';
import { Observable, Subject, take, takeUntil, tap } from 'rxjs';
import { ActiveConfigService } from '../../../../libs/services/active-config/active-config.service';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import { ProgressBarImageSelected } from '../../../../libs/actions/progress-bar-widget-workflow.action';
import { ApiService } from '../../../../libs/services/api-service/api.service';
import { AppBuilderChangesStatusService } from '../../../../libs/services/app-builder-changes-service/app-builder-changes-status.service';
import { WorkflowConfigService } from '../../../../libs/services/workflow-config/workflow-config.service';
import { WorkflowCommonService } from '../../../../libs/services/workflow-config/workflow.common.service';
import { AppGridState } from '../../../../libs/state/app-grid.state';
import { ProjectsState, WorkflowProjectType } from '../../../../libs/state/project.state';
import { AutoDestroy } from '../../../../libs/utils/utils';
import { ProjectIconsService } from '../../../../libs/services/project-icons/project-icons-service.service';

@Component({
  standalone: false,
  selector: 'finxone-workflow-frontend-icon-upload-popup',
  templateUrl: './icon-upload-popup.component.html',
  styleUrls: ['../image-upload-popup.component.scss'],
})
export class IconUploadPopupComponent implements OnInit, OnChanges {
  @AutoDestroy destroy$ = new Subject<void>();
  @Input() public isFromGlobalStyling = false;
  @Input() popupSetting: PopupSettings = {
    show: false,
    type: PopupSettingType.ICON_LIBRARY,
  };

  public files: any[] = [];
  uniqueId: number | string;
  errorMessages: string[] = [];
  uploadProgress = 0;
  showIndividualMenu: { [key: number]: boolean } = {};
  processingImages = true;
  workflowConfig!: WorkflowConfig;
  activeConfig: ActiveConfig;
  apiResponse: any[] = [];
  isShowLogoImages = false;
  defaultImages: LogosResultTypeList[];
  selectedlogo: LogosResultTypeList = {
    id: '',
    filename: '',
    path: '',
    global_status: false,
  };
  isCurrentLogoid: string;
  isShowMoreOption = false;
  pageNumber = 1;
  protected readonly PopupSettingType = PopupSettingType;
  public activeProject: WorkflowProjectType;

  @Select(AppGridState.getNewWidgetDetails)
  newWidgetDetails$!: Observable<{
    widgetName: string;
    uniqueId: string | number;
  }>;

  public imageAcceptanceType = 'image/svg+xml';
  widgetDetails: { widgetName: string; uniqueId: string | number } | undefined;

  constructor(
    private apiService: ApiService,
    private workflowConfigService: WorkflowConfigService,
    private workflowCommonService: WorkflowCommonService,
    private activeConfigService: ActiveConfigService,
    private appBuilderChangesStatusService: AppBuilderChangesStatusService,
    private store: Store,
    private alertHandlerService: AlertHandlerService,
    public projectIconsService: ProjectIconsService,
  ) {
    this.workflowConfigService
      .getConfig()
      .pipe(takeUntil(this.destroy$))
      .subscribe((config: WorkflowConfig) => {
        this.workflowConfig = config;
      });
  }

  ngOnInit(): void {
    this.activeProject = this.store.selectSnapshot(ProjectsState.getActiveProject);
    this.activeConfigService
      .getConfig()
      .pipe(takeUntil(this.destroy$))
      .subscribe((config: ActiveConfig) => {
        this.activeConfig = config;
      });
  }

  ngOnChanges() {
    this.pageNumber = 1;
    this.onGetImages();
  }

  onGetImages() {
    if (this.activeProject?.id) {
      this.apiService
        .getProjectIcons(this.activeProject.id, this.pageNumber, ImageType.ICON)
        .pipe(take(1))
        .subscribe((res: LogosTypeList) => {
          res.result.forEach((icon: LogosResultTypeList) => {
            this.projectIconsService.addProjectIconToList(icon);
          });
          this.onSetImages(res);
        });
    }
  }

  onSetImages(res: LogosTypeList) {
    const resultArray = res.result;
    if (this.pageNumber > 1) {
      for (const logo of resultArray) {
        this.defaultImages.push(logo);
      }
    } else {
      this.defaultImages = resultArray;
    }
  }

  isSVGFile(fileName: string) {
    return /.svg/.exec(fileName);
  }

  /**
   * on file drop handler
   */
  onFileDropped(event: any) {
    this.prepareFilesList(event);
  }

  /**
   * handle file from browsing
   */
  fileBrowseHandler(event: any) {
    this.prepareFilesList(event.target.files);
  }

  /**
   * Convert Files list to normal array list
   * @param files (Files List)
   */
  prepareFilesList(files: Array<any>) {
    for (const item of files) {
      item.hasSizeError = false;
      item.progress = 0;
      if (
        this.popupSetting.type === PopupSettingType.IMAGE_WIDGET ||
        this.popupSetting.type === PopupSettingType.IMAGE_LIBRARY
      ) {
        this.files = [];
      }
      this.files.push(item);
      if (this.files.length > 0) this.showIndividualMenu[this.files.indexOf(item)] = false;
      if (item.size > 2097152) {
        const msg = 'Cant upload ' + item.name + ', it exceeds the limit of 2 MB.';
        this.errorMessages.push(msg);
        item.hasSizeError = true;
      }
    }
    const filesToUpload = this.files.filter((file: any) => {
      return file.hasSizeError === false;
    });
    this.processingImages = false;
    this.uploadFiles(filesToUpload);
  }

  /**
   *
   * @param files  (Image Files List)
   */
  uploadFiles(files: File[]) {
    const formData: FormData = new FormData();
    files.forEach((file: File) => {
      formData.append('myfiles', file);
    });
    const projectId = this.store.selectSnapshot(ProjectsState.getActiveProject).id;
    formData.append('projectId', projectId);
    formData.append('category', ImageType.ICON);

    this.apiService
      .uploadFormData('/file/upload-file', formData)
      .pipe(
        tap((event: any) => {
          this.appBuilderChangesStatusService.appBuilderStatus = 'true';
          if (event.type === HttpEventType.UploadProgress) {
            this.uploadProgress = Math.round((100 * event.loaded) / event.total);
          }
          if (event.loaded === 100) {
            this.processingImages = true;
          }
        }),
        tap((event: HttpEvent<any>) => {
          if (event instanceof HttpResponse) {
            this.alertHandlerService.showAlertFn('success', 'You successfully updated the file.');
            this.processingImages = false;
            this.apiResponse = event.body;
            this.pageNumber = 1;
            this.onGetImages();
          }
        }),
        tap({
          error: (error: any) => {
            let msg;
            if (typeof error.error.message === 'string') {
              msg = error.error.message;
            } else {
              msg = error.error.message.join(', ');
            }

            this.alertHandlerService.showAlertFn('error', msg);
            this.processingImages = false;
            this.files.forEach((file: any) => {
              file.hasSizeError = true;
            });
          },
        }),
      )
      .subscribe();
  }

  updateWorkflowConfigData(response: any, files?: File[]): void {
    this.workflowCommonService.updateImageWidgetConfig(
      this.uniqueId,
      response,
      (this.popupSetting?.type as WidgetNames) ?? WidgetNames.IMAGE_WIDGET,
      files,
    );
  }

  closePopup(type: string) {
    if (type == 'cancel') {
      this.popupSetting.show = false;
      this.files = [];
      this.errorMessages = [];
      if (
        this.popupSetting.type !== PopupSettingType.PAGE_BACKGROUND &&
        this.popupSetting.type !== PopupSettingType.IMAGE_LIBRARY &&
        this.popupSetting.type !== WidgetNames.CARD
      ) {
        this.removeWidgetFromConfig();
      }
      this.apiResponse = [];
      this.processingImages = false;
    } else {
      const selectedlogos = this.defaultImages.filter((x) => x.isActive === true);
      if (selectedlogos.length) {
        this.updateWorkflowConfigData(selectedlogos);
      }

      this.popupSetting.show = false;
      this.files = [];
      this.errorMessages = [];
      this.apiResponse = [];
      this.processingImages = false;
    }
  }

  removeWidgetFromConfig() {
    this.workflowCommonService.removeLastAddedWidget();
  }

  deleteLogo(id: string) {
    const payload: LogodeletePayload = {
      id: [id],
      projectId: this.activeProject.id,
    };
    this.apiService.deletelogo(payload).subscribe(() => {
      this.appBuilderChangesStatusService.appBuilderStatus = 'true';
      this.pageNumber = 1;
      this.onGetImages();
      this.alertHandlerService.showAlertFn('success', 'Delete Logo Successfully');
    });
  }

  showMore(id: string) {
    if (this.isCurrentLogoid == id) {
      this.isShowMoreOption = !this.isShowMoreOption;
    } else {
      this.isShowMoreOption = true;
    }

    this.isCurrentLogoid = id;
  }

  onScroll(event: { target: any }) {
    if (Math.ceil(event.target.offsetHeight + event.target.scrollTop) >= event.target.scrollHeight) {
      this.pageNumber = this.pageNumber + 1;
      this.onGetImages();
    }
  }
}
